import {
  Box,
  Chip,
  Stack,
  InputBase,
  Collapse,
  TextField,
  CircularProgress,
  Button,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useDataProvider, useNotify, useGetList, useGetOne, useGetIdentity } from "react-admin";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  TaskStatus,
  Task,
  ObjectType,
  TaskRecordReference,
  TaskTemplate,
  ResourceName,
  AlertType,
  TaskCreate,
  DateYMDString,
  User,
  UserStatus,
} from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { addDays, templateAppliesTo } from "../../utils/taskUtils";

// Shared task components
import { AssigneeSelector } from "../shared/AssigneeSelector";
import { DueDateSelector } from "../shared/DueDateSelector";
import { NotesChip } from "../shared/NotesChip";
import { RecordSelector } from "../shared/RecordSelector";
import { getSuggestionChipStyles } from "../shared/taskChipStyles";

interface TaskComposerInlineProps {
  record: TaskRecordReference;
  onCreated?: (task: Task) => void;
  defaultStatus?: TaskStatus;
  open?: boolean;
  /**
   * Client records only: also offer claimant templates, and create a task from
   * one on a claimant of this client picked inline rather than on the client
   * itself (#7655).
   */
  claimantClientId?: string;
}

// Templates shown as quick-add chips; the rest sit behind a "+N more" chip.
const QUICK_ADD_CHIPS = 4;

export function TaskComposerInline({
  record,
  onCreated,
  defaultStatus = TaskStatus.OPEN,
  open,
  claimantClientId,
}: TaskComposerInlineProps) {
  const { canCreate } = useResourcePermissions(ResourceName.TASKS);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const { data: currentUser } = useGetIdentity();

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [showDescription, setShowDescription] = useState(false);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [dueDate, setDueDate] = useState<DateYMDString | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [claimant, setClaimant] = useState<{ id: string; label?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  // Which inline selector's menu/popover is currently open. Tracked here (not
  // just inside each selector) so the chip row stays mounted while a menu is
  // open and so we can force-close before the drawer collapses — otherwise a
  // MUI menu unmounting while open leaked a body scroll-lock and froze the
  // drawer (#5158 / #6310 AC4).
  const [openSelector, setOpenSelector] = useState<"assignee" | "due" | "templates" | null>(null);
  const moreTemplatesRef = useRef<HTMLDivElement>(null);

  // Pre-fetch active users so we can guard the auto-assign below.
  // react-query deduplicates the HTTP call — AssigneeSelector uses the same query key.
  const { data: activeUsers = [], isLoading: isLoadingUsers } = useGetList<User>(ResourceName.USERS, {
    pagination: { page: 1, perPage: 100 },
    filter: { status: UserStatus.ACTIVE },
  });

  // Set default assignee to current user only if they are provisioned in this tenant.
  // Skipping this check caused a 404 → retry storm that locked the task drawer (Bug #5158).
  // Guard on isLoadingUsers so the effect doesn't re-run on every render while the list loads.
  useEffect(() => {
    if (isLoadingUsers || !currentUser?.id || assigneeId) return;
    const currentUserId = String(currentUser.id);
    if (activeUsers.some((u) => String(u.id) === currentUserId)) {
      setAssigneeId(currentUserId);
    }
  }, [currentUser?.id, activeUsers, isLoadingUsers]);

  // Data fetching. Every active template, filtered client-side (#7655). No type
  // filter on the request: a client record also offers claimant templates, so a
  // server-side filter on this record's type would drop them. 1000 is the API's
  // MaxPageSize; the title-sorted list otherwise stops at the default page of 50.
  const { data: templates = [] } = useGetList<TaskTemplate>(
    "task-templates",
    {
      pagination: { page: 1, perPage: 1000 },
      filter: { is_active: true },
    },
    { enabled: !!record.object_type }
  );

  // This record's own templates first, then (client records) claimant templates.
  const offeredTemplates = useMemo(() => {
    const own = templates.filter((t) => templateAppliesTo(t, record.object_type));
    if (!claimantClientId) return own;
    const forClaimants = templates.filter((t) => !own.includes(t) && templateAppliesTo(t, ObjectType.CLAIMANTS));
    return [...own, ...forClaimants];
  }, [templates, record.object_type, claimantClientId]);

  const { data: selectedTemplate } = useGetOne<TaskTemplate>(
    "task-templates",
    { id: templateId || "" },
    { enabled: !!templateId }
  );

  // Apply template
  useEffect(() => {
    if (!selectedTemplate) return;
    setTitle(selectedTemplate.title || "");
    if (selectedTemplate.description) {
      setDescription(selectedTemplate.description);
      setShowDescription(true);
    }
    if (selectedTemplate.default_due_days) {
      setDueDate(addDays(selectedTemplate.default_due_days) as DateYMDString);
    }
  }, [selectedTemplate]);

  // A claimant template applied on a client record targets a claimant, not the client.
  const targetsClaimant =
    !!claimantClientId && !!selectedTemplate && !templateAppliesTo(selectedTemplate, record.object_type);

  const canSubmit = title.trim().length > 0 && !saving && (!targetsClaimant || !!claimant);

  const resetForm = useCallback(() => {
    setTitle("");
    setDescription("");
    setShowDescription(false);
    setDueDate(null);
    setTemplateId(null);
    setClaimant(null);
    setOpenSelector(null);
    // Keep assignee for quick successive adds
  }, []);

  useEffect(() => {
    if (open === false) {
      resetForm();
      setIsFocused(false);
    }
  }, [open, resetForm]);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSaving(true);

    try {
      const taskData: TaskCreate = {
        title: title.trim(),
        status: defaultStatus,
        completed: defaultStatus === TaskStatus.COMPLETED,
        display_claim_exhibit: true,
        partner_id_ref: currentUser?.partner_id_ref, // or wherever this comes from
        record:
          targetsClaimant && claimant
            ? {
                object_type: ObjectType.CLAIMANTS,
                object_id: claimant.id,
                label: claimant.label || `${ObjectType.CLAIMANTS} ${claimant.id}`,
              }
            : {
                object_type: record.object_type,
                object_id: record.object_id,
                label: record.label || `${record.object_type} ${record.object_id}`,
              },
      };

      if (description.trim()) taskData.description = description.trim();
      if (dueDate) taskData.due_date = dueDate;
      if (assigneeId) taskData.assigned_user_id_ref = assigneeId;
      if (templateId) {
        taskData.template_id_ref = templateId;
        if (!assigneeId && selectedTemplate?.auto_assign_role_id_ref) {
          taskData.assigned_role_id_ref = selectedTemplate.auto_assign_role_id_ref;
        }
      }

      const { data: newTask } = await dataProvider.create<Task>(ResourceName.TASKS, { data: taskData });

      notify("Task created", { type: AlertType.SUCCESS });
      resetForm();
      onCreated?.(newTask);
    } catch (error) {
      notifyError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && canSubmit) {
      e.preventDefault();
      void handleSubmit();
    }
  };

  if (!canCreate) return null;

  const suggestionChip = getSuggestionChipStyles({ size: "small" });
  // Keep the chip row mounted while a selector is open, so its menu never
  // unmounts mid-open (see openSelector note above).
  const showChips = isFocused || title.length > 0 || openSelector !== null;

  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
      {/* Quick add suggestions */}
      {/* Stays open while the "more" menu is, which takes focus from the input. */}
      <Collapse in={offeredTemplates.length > 0 && !title && (isFocused || openSelector === "templates")}>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {offeredTemplates.slice(0, QUICK_ADD_CHIPS).map((t) => (
            // preventDefault on mousedown keeps focus on the input. Otherwise the
            // input's blur collapses this row before mouseup, and second-row chips
            // are clipped out from under the pointer — the click never fires.
            <Chip
              key={t.id}
              label={t.title}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setTemplateId(t.id)}
              sx={suggestionChip}
              size="small"
            />
          ))}
          {offeredTemplates.length > QUICK_ADD_CHIPS && (
            <Chip
              ref={moreTemplatesRef}
              label={`+${offeredTemplates.length - QUICK_ADD_CHIPS} more`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setOpenSelector("templates")}
              sx={suggestionChip}
              size="small"
            />
          )}
        </Stack>
        <Menu
          anchorEl={moreTemplatesRef.current}
          open={openSelector === "templates"}
          onClose={() => setOpenSelector(null)}
          disableScrollLock
          slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 220, maxHeight: 320 } } }}
        >
          {offeredTemplates.slice(QUICK_ADD_CHIPS).map((t) => (
            <MenuItem
              key={t.id}
              onClick={() => {
                setTemplateId(t.id);
                setOpenSelector(null);
              }}
            >
              {t.title}
            </MenuItem>
          ))}
        </Menu>
      </Collapse>

      {/* Title input row */}
      <Stack direction="row" spacing={1} alignItems="center">
        <AddIcon sx={{ fontSize: 18, color: "text.disabled" }} />
        <InputBase
          placeholder="Add a task..."
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            // Clearing the title detaches the template (and any claimant it targets),
            // so the chips come back live and a typed title binds to this record.
            if (!e.target.value && templateId) {
              setTemplateId(null);
              setClaimant(null);
            }
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => !title && setIsFocused(false)}
          onKeyDown={handleKeyDown}
          disabled={saving}
          fullWidth
          sx={{
            fontSize: 14,
            "& input::placeholder": { color: "text.disabled", opacity: 1 },
          }}
        />
        {title && !saving && (
          <IconButton
            size="small"
            onClick={() => {
              resetForm();
              setIsFocused(false);
            }}
            sx={{ p: 0.25, color: "text.disabled" }}
            aria-label="Clear task"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
        {saving && <CircularProgress size={16} />}
      </Stack>

      {/* Chips row - using shared components with small size */}
      <Collapse in={showChips}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 1, ml: 3.5 }} flexWrap="wrap" useFlexGap>
          {targetsClaimant && claimantClientId && (
            <RecordSelector
              objectType={ObjectType.CLAIMANTS}
              objectId={claimant?.id ?? null}
              label={claimant?.label}
              editable
              lockType
              filter={{ client_id_ref: claimantClientId }}
              placeholder="Select claimant..."
              onChange={(_, id, label) => setClaimant(id ? { id, label } : null)}
            />
          )}

          <AssigneeSelector
            value={assigneeId}
            onChange={setAssigneeId}
            size="small"
            open={openSelector === "assignee"}
            onOpenChange={(next) => setOpenSelector(next ? "assignee" : null)}
          />

          <DueDateSelector
            value={dueDate}
            onChange={setDueDate}
            size="small"
            disablePast
            open={openSelector === "due"}
            onOpenChange={(next) => setOpenSelector(next ? "due" : null)}
          />

          {!showDescription && <NotesChip onClick={() => setShowDescription(true)} size="small" />}

          {/* Create button */}
          {title.trim() && (
            <Button variant="contained" size="small" onClick={handleSubmit} disabled={!canSubmit} sx={{ ml: "auto" }}>
              Create
            </Button>
          )}
        </Stack>
      </Collapse>

      {/* Description field */}
      <Collapse in={showDescription}>
        <Box sx={{ mt: 1, ml: 3.5 }}>
          <TextField
            placeholder="Add notes..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            fullWidth
            size="small"
            disabled={saving}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
